import type { DeviceUiPhase, FormStepId } from '@/types/deviceSetup';
import { staticDefaults } from '@/config/static/catalog';

/** Figma section: Add-device-controller-flow-novice */
export const ADD_DEVICE_FIGMA_SECTION = 'Add-device-controller-flow-novice' as const;

export const ADD_DEVICE_FLOW_TITLE = 'Add a controller to this system';

export const DEVICE_SETUP_TRUST_LINE =
  'AI proposes, you approve. No auto-actions on devices, users, or credentials.';

/** Maps UI phases to reference assets (placeholders until Figma exports land). */
export const NOVICE_ADD_DEVICE_SCREEN_ASSETS: Record<string, string> = {
  'home-entry': '/figma-screens/novice-home-1.png',
  thinking: '/figma-screens/operator-flow-1.png',
  working: '/figma-screens/operator-flow-2.png',
  'almost-ready': '/figma-screens/operator-flow-3.png',
  preview: '/figma-screens/operator-flow-4.png',
  'form-type': '/figma-screens/operator-flow-3.png',
  'form-manufacturer': '/figma-screens/operator-flow-4.png',
  'form-details': '/figma-screens/operator-flow-5.png',
  confirm: '/figma-screens/operator-flow-6.png',
  saving: '/figma-screens/operator-flow-5.png',
  success: '/figma-screens/operator-flow-6.png',
};

export const ADD_DEVICE_UI_PHASES: { id: DeviceUiPhase; figmaLabel: string; assetKey: string }[] = [
  { id: 'thinking', figmaLabel: 'Thinking...', assetKey: 'thinking' },
  { id: 'working', figmaLabel: 'Working on it...', assetKey: 'working' },
  { id: 'almost-ready', figmaLabel: 'Almost ready..', assetKey: 'almost-ready' },
  { id: 'preview', figmaLabel: 'Preview + How I figured this out', assetKey: 'preview' },
  { id: 'form', figmaLabel: 'Guided form (type → manufacturer → details → confirm)', assetKey: 'form-type' },
  { id: 'saving', figmaLabel: 'Saving', assetKey: 'saving' },
  { id: 'success', figmaLabel: 'Controller added', assetKey: 'success' },
];

export const FORM_STEPS: { id: FormStepId; label: string }[] = [
  { id: 'device-type', label: 'Select device type' },
  { id: 'manufacturer', label: 'Manufacturer & model' },
  { id: 'details', label: 'Details' },
  { id: 'confirm', label: 'Confirm' },
];

export const PROGRESS_RAIL_STEPS = [
  { id: 'access', label: 'Checking your access' },
  { id: 'system', label: 'Confirming the system' },
  { id: 'order', label: 'Following the setup order' },
  { id: 'preview', label: 'Building your preview' },
] as const;

/** Simulated loading pacing (ms). Middle entries hold ~3s on the center progress steps. */
export const DEVICE_SETUP_TIMING = {
  thinkingMs: 900,
  /** Delay while each progress step is active before advancing */
  progressStepMs: [750, 3000, 3000, 800] as const,
  almostReadyMs: 700,
  formRevealMs: 500,
  savingMs: 1200,
} as const;

export const DEVICE_TYPE_OPTIONS = [
  {
    id: 'door-controller' as const,
    title: 'Door controller',
    description:
      'The access control panel the system reaches on the network. Doors and readers are set up on this panel after it is added.',
  },
  {
    id: 'video-recorder' as const,
    title: 'Video recorder',
    description: 'Video hardware or recorder on the network for cameras tied to this site.',
  },
];

/** @deprecated Use staticDefaults.device.manufacturers */
export const DUMMY_MANUFACTURERS: Record<string, string[]> = staticDefaults.device.manufacturers;

/** @deprecated Use staticDefaults.device.defaultDraft */
export const DUMMY_DEFAULT_DRAFT = { ...staticDefaults.device.defaultDraft };

/** @deprecated Use staticDefaults.device.success */
export const DUMMY_SUCCESS = { ...staticDefaults.device.success };

/** @deprecated Use staticDefaults.device.explainability */
export const DUMMY_EXPLAINABILITY = staticDefaults.device.explainability;

/** @deprecated Use staticDefaults.device.assistantHint */
export const DUMMY_ASSISTANT_SAVING = staticDefaults.device.assistantHint;

export function assetForPhase(phase: DeviceUiPhase, formStep?: FormStepId): string {
  if (phase === 'form' && formStep) {
    const key =
      formStep === 'device-type'
        ? 'form-type'
        : formStep === 'manufacturer'
          ? 'form-manufacturer'
          : formStep === 'details'
            ? 'form-details'
            : 'confirm';
    return NOVICE_ADD_DEVICE_SCREEN_ASSETS[key] ?? NOVICE_ADD_DEVICE_SCREEN_ASSETS['form-type'];
  }
  return NOVICE_ADD_DEVICE_SCREEN_ASSETS[phase] ?? NOVICE_ADD_DEVICE_SCREEN_ASSETS.thinking;
}
